import { ModalCustom } from "@/components/Common"
import { Button } from "@/components/ui/button"
import { Stepper, StepperNavigation } from "@/components/ui/stepper"
import { useBookingQuery } from "@/query/bookingQuery"
import { useClientNameQuery } from "@/query/clientQuery"
import {
  CollectionState,
  nextWithAppointmentStep,
  previousWithAppointmentStep,
  resetWithAppointmentInfo,
  setCollectionInfo,
  setWithAppointmentStep,
} from "@/store/cashRegisterReducers/cashRegisterSlice"
import { setOpenModalCashRegisterWithAppointment } from "@/store/modalsReducers/modalSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { Calendar, CreditCard, FileCheck, Package, User } from "lucide-react"
import React, { useEffect } from "react"
import { UnifiedPaymentStep } from "../commons/unifiedPayment/UnifiedPaymentStep"
import { ValidateServiceStep } from "../commons/validateService/ValidateServiceStep"
import { ReservationSelectionList } from "./appointmentSelection/ReservationSelectionList"

// Définition des étapes avec leurs informations
const STEPS = [
  {
    id: "choiceAppointment",
    title: "Choix du RDV",
    icon: Calendar,
  },
  {
    id: "validateService",
    title: "Validation Service",
    icon: Package,
  },
  {
    id: "validatePayment",
    title: "Paiement",
    icon: CreditCard,
  },
  {
    id: "validate",
    title: "Finalisation",
    icon: FileCheck,
  },
]

export const CashRegisterWithAppointmentModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const { booking: reservation } = useBookingQuery()
  const { openModalCashRegisterWithAppointment } = useAppSelector(
    (state) => state.modal
  )
  const { collectionInfo, bookingId } = useAppSelector(
    (state) => state.cashRegister
  )

  // Récupérer la réservation sélectionnée depuis le store
  const selectedBooking = bookingId
  const currentReservation = reservation?.find(
    (r) => r.bookingId === selectedBooking
  )

  // Initialiser l'état si nécessaire
  useEffect(() => {
    if (openModalCashRegisterWithAppointment.isOpen && !collectionInfo) {
      dispatch(
        setCollectionInfo({
          collectionState: "choiceAppointment",
          services: [],
          discountAppliedGlobal: null,
          serviceWithPromo: [],
          keysUsed: [],
        })
      )
    }
  }, [openModalCashRegisterWithAppointment.isOpen, collectionInfo, dispatch])

  const handleClose = () => {
    dispatch(setOpenModalCashRegisterWithAppointment({ isOpen: false }))
    dispatch(resetWithAppointmentInfo())
  }

  const handleStepChange = (stepId: string) => {
    dispatch(setWithAppointmentStep(stepId as CollectionState))
  }

  const handleNext = () => {
    dispatch(nextWithAppointmentStep())
  }

  const handlePrevious = () => {
    dispatch(previousWithAppointmentStep())
  }

  const handleFinish = () => {
    // Logique de finalisation à implémenter plus tard
    handleClose()
  }

  if (!collectionInfo) {
    return null
  }

  const currentStep = collectionInfo.collectionState
  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep)
  const currentStepData = STEPS[currentStepIndex]
  const canGoBack = currentStepIndex > 0
  const canGoNext =
    currentStepIndex < STEPS.length - 1 &&
    (currentStep !== "choiceAppointment" || !!selectedBooking)
  const isLastStep = currentStepIndex === STEPS.length - 1

  // Rendu du contenu de l'étape actuelle
  const renderStepContent = () => {
    const IconComponent = currentStepData.icon

    switch (currentStep) {
      case "choiceAppointment":
        return (
          <div className="space-y-4 h-full bg-background">
            <ReservationSelectionList />
          </div>
        )

      case "validateService":
        return (
          <div className="h-full">
            <ValidateServiceStep />
          </div>
        )

      case "validatePayment":
        return (
          <div className="h-full">
            <UnifiedPaymentStep />
          </div>
        )

      case "validate":
        return (
          <div className="text-center">
            <IconComponent className="h-16 w-16 mx-auto mb-4 text-green-600" />
            <h3 className="text-lg font-semibold mb-2">Finalisation</h3>
            <p className="text-gray-600 mb-4">
              Terminer l'encaissement et imprimer le reçu
            </p>
            {currentReservation && (
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-green-700 mb-1">
                  Réservation : {currentReservation.bookingId}
                </p>
                <p className="text-sm text-green-700">Montant total : {0}€</p>
              </div>
            )}
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">
                Prêt à finaliser l'encaissement
              </p>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <ModalCustom
      isOpen={openModalCashRegisterWithAppointment.isOpen}
      onClose={handleClose}
      maxWidth={95}
      maxHeight={95}
    >
      <div className="flex flex-col flex-1 bg-background">
        {/* Header personnalisé */}
        <div className="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-gray-700 p-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 text-primary-foreground rounded-lg">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Encaissement Avec Rendez-vous
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Encaissement de rendez-vous terminés et prestations planifiées
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {currentReservation && (
              <ClientNameDisplay clientId={currentReservation.clientId!} />
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              ✕
            </Button>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-6 mb-6">
          <Stepper
            steps={STEPS.map((step) => ({
              id: step.id,
              title: step.title,
            }))}
            currentStep={currentStep}
            onStepChange={handleStepChange}
          />
        </div>

        {/* Contenu de l'étape */}
        <div className="flex-1 px-6 pb-6">{renderStepContent()}</div>

        {/* Navigation */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-2">
          <StepperNavigation
            canGoBack={canGoBack}
            canGoNext={canGoNext}
            onBack={handlePrevious}
            onNext={handleNext}
            onFinish={handleFinish}
            isLastStep={isLastStep}
            backLabel="Précédent"
            nextLabel="Suivant"
            finishLabel="Finaliser"
          />
        </div>
      </div>
    </ModalCustom>
  )
}

const ClientNameDisplay: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { client, isLoading, isError } = useClientNameQuery(clientId)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <User className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Chargement...
        </p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2">
        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <User className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{clientId}</p>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
        <User className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {client?.name || clientId}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {client?.email || clientId}
        </p>
      </div>
    </div>
  )
}
